import styles from "./YourPlan.module.scss";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import SubscriptionItem from "./SubscriptionItem/SubscriptionItem";
import useAppSelector from "../../../../hooks/useAppSelector";
import ConfirmPopup from "../../../../components/shared/ConfirmPopup/ConfirmPopup";
import attentionIcon from "../../../../assets/images/icons/attention.svg";
import AddonItem from "./AddonItem/AddonItem";
import { useGetAllSubscriptionsQuery, useGetStripePublicKeyQuery } from "../../../../api/queries";
import { useGetAllAddonsQuery } from "../../../../api/queries";
import { useGetUserSubscriptionQuery } from "../../../../api/queries";
import { useNavigate } from "react-router";
import { useStripeMutations } from "../../../../api/mutations";
import { handleError } from "../../../../helper/errorHandler";
import useAppDispatch from "../../../../hooks/useAppDispatch";
import FullScreenLoader from "../../../../components/shared/FullScreenLoader/FullScreenLoader";

const YourPlan = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state) => state.userProfileSlice);
  const { data: subscriptions = [] } = useGetAllSubscriptionsQuery();
  const { data: addons = [] } = useGetAllAddonsQuery();
  const { data: userSubscription, refetch: refetchUserSubscription } = useGetUserSubscriptionQuery(
    user?.id || null
  );
  const { data } = useGetStripePublicKeyQuery();
  const stripePublicKey = data?.publicKey ?? "";

  const { resumeSubscriptionCancellation } = useStripeMutations();

  const [showMore, setShowMore] = useState<boolean>(false);
  const initialConfirmPopupValues = {
    isOpen: false,
    title: "",
    description: "",
    icon: attentionIcon,
    buttonText: "",
  };
  const [showConfirmPopup, setShowConfirmPopup] = useState(initialConfirmPopupValues);
  const [loading, setLoading] = useState(false);

  const changeShowMoreVisible = () => {
    setShowMore(!showMore);
  };

  const clickNoOnConfirmPopup = () => {
    setShowConfirmPopup(initialConfirmPopupValues);
  };

  const clickYesOnConfirmPopup = () => {
    if (!userSubscription) return;
    if (!userSubscription.cancelAt) {
      navigate("/settings/account/manage-plan/cancel");
    } else {
      setLoading(true);
      resumeSubscriptionCancellation(userSubscription.id, {
        onSuccess: () => {
          setLoading(false);
          clickNoOnConfirmPopup();
          refetchUserSubscription();
        },
        onError: (e) => {
          clickNoOnConfirmPopup();
          setLoading(false);
          handleError(e, dispatch, t);
        },
      });
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t("YourPlan")}</h2>
      <p className={styles.subtitle}>
        {t("YouCanChangeYourPlanImmediatelyOrSelectPlanThatWorksBestForYouAfterYourTrial")}
      </p>
      <div className={styles.subscriptionsWrapper}>
        {userSubscription && (
          <div className={styles.userSubscription}>
            <h3 className={styles.name}>{userSubscription.subscription.name}</h3>
            <h3 className={styles.credits}>
              {userSubscription.subscription.credits} {t("Credits")}
            </h3>
            <p className={styles.price}>€{userSubscription.subscription.price} / mo</p>
            {userSubscription.subscription.description.length > 37 && !showMore ? (
              <p className={styles.description}>
                {userSubscription.subscription.description.slice(0, 33)}
                <span onClick={changeShowMoreVisible} className={styles.showMore}>
                  {t("ShowMore")}..
                </span>
              </p>
            ) : (
              <p className={styles.description}>
                {userSubscription.subscription.description}
                {showMore && (
                  <span onClick={changeShowMoreVisible} className={styles.showMore}>
                    {t("ShowLess")}
                  </span>
                )}
              </p>
            )}
            <div className={styles.border}></div>
            <p className={styles.date}>52 credits left - Trial ends November 24</p>
          </div>
        )}
        {!!subscriptions.length &&
          subscriptions.map((el) => {
            return <SubscriptionItem key={el.id} item={el} stripePublicKey={stripePublicKey} />;
          })}
      </div>

      {userSubscription && (
        <>
          <div className={styles.border}></div>
          {userSubscription.cancelAt ? (
            <div
              onClick={() =>
                setShowConfirmPopup({
                  isOpen: true,
                  icon: attentionIcon,
                  title: t("ConfirmResumeSubscription"),
                  description: t("AreYouSureYouWantToResumeSubscription"),
                  buttonText: t("Resume"),
                })
              }
              className={styles.cancelMembership}
            >
              {t("ResumeCancellation")}
            </div>
          ) : (
            <div
              onClick={() =>
                setShowConfirmPopup({
                  isOpen: true,
                  icon: attentionIcon,
                  title: t("ConfirmUnsubscription"),
                  description: t("AreYouSureYouWantToUnsubscribe"),
                  buttonText: t("Unsubscribe"),
                })
              }
              className={styles.cancelMembership}
            >
              {t("CancelMembership")}
            </div>
          )}

          <div className={styles.border}></div>
          {!!addons.length && (
            <div className={styles.addons}>
              <h2 className={styles.title}>{t("Addons")}</h2>
              <p className={styles.subtitle}>
                {t("YouCanBuyExtraCreditsWithoutAffectingYourCurrentSubscription")}
              </p>
              <div className={styles.addonsWrapper}>
                {addons.map((el) => {
                  return <AddonItem key={el.id} item={el} stripePublicKey={stripePublicKey} />;
                })}
              </div>
            </div>
          )}
        </>
      )}
      {showConfirmPopup.isOpen && (
        <ConfirmPopup
          iconPath={showConfirmPopup.icon}
          title={showConfirmPopup.title}
          description={showConfirmPopup.description}
          onClickYes={clickYesOnConfirmPopup}
          onClickNo={clickNoOnConfirmPopup}
          textForConfirmBtn={showConfirmPopup.buttonText}
          textForRejectBtn={t("Cancel")}
        />
      )}
      {loading && <FullScreenLoader />}
    </div>
  );
};

export default YourPlan;
